import { useCallback, useMemo, useRef, useEffect, useState } from 'react';
import { Alert, Box, Stack, Typography } from '@mui/material';
import { DataGrid, GridRow } from '@mui/x-data-grid';
import { COLORS, DATE_FORMATS } from '../../../../utils/Application_Constants';
import { formatUtcDate } from '../../../../utils/formatDate';
import { HIERARCHY_BORDER_STYLE } from '../styles';
import { decimalFormatter, currencyFormatter } from '../../../../utils/helpers';
import DataGridSortIcon from '../../../../Shared/Components/DataGridSortIcon';
import useDebounce from '../../../../utils/useDebounce';

const TABLE_BORDER_STYLE = `1px solid ${COLORS.LT_MIDNIGHT10}`;

const timesheetLineItemColumns = [
    {
        field: 'date',
        headerName: 'Txn Date',
        flex: 1,
        valueFormatter: (value) => formatUtcDate(value, DATE_FORMATS.WEEKDAY_MD),
        cellClassName: 'bold-text',
    },
    { field: 'rateType', headerName: 'Rate Type', flex: 1 },
    {
        field: 'billTime',
        headerName: 'Bill Time',
        flex: 1,
        sortable: false,
        headerClassName: 'left-border',
        cellClassName: 'left-border',
        valueFormatter: (value) => decimalFormatter(value),
    },
    { field: 'billUnit', headerName: 'Bill Unit', flex: 1, sortable: false },
    {
        field: 'billRate',
        headerName: 'Bill Rate',
        flex: 1,
        sortable: false,
        valueFormatter: (value) => currencyFormatter(value),
    },
    {
        field: 'billAmount',
        headerName: 'Bill Amount',
        flex: 1,
        sortable: false,
        valueFormatter: (value) => currencyFormatter(value),
    },
    {
        field: 'payTime',
        headerName: 'Pay Time',
        flex: 1,
        sortable: false,
        headerClassName: 'left-border',
        cellClassName: 'left-border',
        valueFormatter: (value) => decimalFormatter(value),
    },
    { field: 'payUnit', headerName: 'Pay Unit', flex: 1, sortable: false },
    {
        field: 'payRate',
        headerName: 'Pay Rate',
        flex: 1,
        sortable: false,
        valueFormatter: (value) => currencyFormatter(value),
    },
    {
        field: 'payAmount',
        headerName: 'Pay Amount',
        flex: 1,
        sortable: false,
        valueFormatter: (value) => currencyFormatter(value),
    },
];

const createRowWithRef = (setRefsUpdated, lastChildRef, totalRows) => (props) => {
    const { rowId, index } = props;
    const rowRef = useRef(null);

    useEffect(() => {
        if (rowRef.current) {
            setRefsUpdated((prev) => {
                const newRefs = [...prev];

                const refIndex = newRefs.findIndex((ref) => ref.rowId === rowId);

                if (refIndex === -1) {
                    newRefs.push({ rowId, ref: rowRef.current });
                } else {
                    newRefs[refIndex] = { rowId, ref: rowRef.current };
                }

                return newRefs;
            });
        }

        return () => {
            setRefsUpdated((prev) => prev.filter((ref) => ref.rowId !== rowId));
        };
    }, [rowId, setRefsUpdated]);

    return (
        <GridRow
            ref={(ref) => {
                rowRef.current = ref;
                if (totalRows === index + 1) {
                    // eslint-disable-next-line no-param-reassign
                    lastChildRef.current = ref;
                }
            }}
            {...props}
        />
    );
};

const LocationEntries = ({
    locationName,
    lineItems,
    errorMessage,
    hierarchyConnectorVerticalRef,
    lastChildRef,
    selectable = false,
}) => {
    const containerRef = useRef(null);
    const gridHeaderRowRef = useRef(null);
    const [gridRowsRefs, setGridRowsRefs] = useState([]); // [{ rowId: number, ref: ref }]
    const [gridRowConnectorLines, setGridRowConnectorLines] = useState([]);
    // Update connector lines when grid rows refs change
    const updateConnectorLines = useCallback(() => {
        if (!hierarchyConnectorVerticalRef?.current || !containerRef?.current) return;

        const containerRect = containerRef.current.getBoundingClientRect();
        const verticalConnectorRect = hierarchyConnectorVerticalRef.current.getBoundingClientRect();

        const newConnectorLines = [];

        gridRowsRefs.forEach(({ rowId, ref }) => {
            if (ref) {
                const rowRect = ref.getBoundingClientRect();
                // Calculate positions relative to the DataGrid container
                const relativeTop = rowRect.top - containerRect.top;
                const left = verticalConnectorRect.left - rowRect.left;

                newConnectorLines.push({
                    id: rowId,
                    left: left + 1,
                    top: relativeTop + rowRect.height / 2,
                    width: -left,
                });
            }
        });

        // Update header connector line
        if (gridHeaderRowRef?.current) {
            const headerRect = gridHeaderRowRef.current.getBoundingClientRect();
            const relativeTop = headerRect.top - containerRect.top;
            const left = verticalConnectorRect.left - headerRect.left;
            const headerConnectorLine = {
                id: 'header',
                left: left + 1,
                width: -left,
                top: relativeTop + headerRect.height / 2,
            };
            newConnectorLines.push(headerConnectorLine);
        }

        setGridRowConnectorLines(newConnectorLines);
    }, [hierarchyConnectorVerticalRef, gridRowsRefs, gridHeaderRowRef]);

    const debouncedUpdateConnectorLines = useDebounce(updateConnectorLines, 10);

    useEffect(() => {
        // Update on mount and window resize
        debouncedUpdateConnectorLines();

        const handleResize = () => {
            debouncedUpdateConnectorLines();
        };

        // Use ResizeObserver to watch for element size changes
        const resizeObserver = new ResizeObserver(handleResize);
        if (hierarchyConnectorVerticalRef?.current)
            resizeObserver.observe(hierarchyConnectorVerticalRef.current);
        if (containerRef?.current) resizeObserver.observe(containerRef.current);

        window.addEventListener('resize', handleResize);

        return () => {
            resizeObserver.disconnect();
            window.removeEventListener('resize', handleResize);
        };
    }, [debouncedUpdateConnectorLines]);

    const RowWithRefComponent = useMemo(
        () => createRowWithRef(setGridRowsRefs, lastChildRef, lineItems?.length),
        [lineItems?.length]
    );

    return (
        <Box position="relative" ref={containerRef}>
            <Stack direction="row" alignItems="center" gap="18px" mb="18px">
                <Typography sx={{ fontSize: 14, color: COLORS.LT_MIDNIGHT75 }}>
                    {locationName}
                </Typography>
                {errorMessage && (
                    <Alert
                        severity="error"
                        sx={{
                            flexGrow: 1,
                            fontSize: 12,
                            height: 32,
                            py: 0,
                            lineHeight: 'normal',
                            alignItems: 'center',
                        }}
                    >
                        {errorMessage}
                    </Alert>
                )}
            </Stack>
            <DataGrid
                sx={{
                    borderRadius: '5px',
                    border: TABLE_BORDER_STYLE,
                    background: COLORS.WHITE,
                    '& .MuiDataGrid-iconSeparator': {
                        display: 'none',
                    },
                    '& .MuiDataGrid-columnHeaders': {
                        marginBottom: '0px',
                    },
                    '& .MuiDataGrid-row--borderBottom': {
                        '& .MuiDataGrid-columnHeader': {
                            borderBottom: 'none',
                        },
                    },
                    '& .MuiDataGrid-columnHeader': {
                        backgroundColor: '#F4FAFF',
                    },
                    '& .MuiDataGrid-virtualScrollerContent': {
                        borderTop: 'none',
                    },
                    '& .MuiDataGrid-row': {
                        '--DataGrid-rowBorderColor': COLORS.LT_MIDNIGHT10,
                    },
                    '& .left-border': {
                        borderLeft: TABLE_BORDER_STYLE,
                    },
                    '& .bold-text': {
                        fontWeight: 'bold',
                    },
                }}
                autoHeight
                columnHeaderHeight={35}
                rowHeight={48}
                columns={timesheetLineItemColumns}
                rows={lineItems}
                columnResizeIcon={false}
                disableAutosize
                disableColumnMenu
                disableColumnResize
                checkboxSelection={selectable}
                disableSelectionOnClick
                disableRowSelectionOnClick
                slotProps={{ columnHeaders: { ref: gridHeaderRowRef } }}
                slots={{
                    columnHeaderSortIcon: DataGridSortIcon,
                    row: RowWithRefComponent,
                }}
            />

            {/* Horizontal connector lines */}
            {gridRowConnectorLines.map((line) => (
                <Box
                    key={line.id}
                    aria-hidden
                    sx={{
                        position: 'absolute',
                        top: line.top,
                        left: line.left,
                        width: line.width,
                        height: 0,
                        borderTop: HIERARCHY_BORDER_STYLE,
                        transform: 'translateY(-50%)',
                        pointerEvents: 'none',
                        zIndex: 1,
                    }}
                />
            ))}
        </Box>
    );
};

export default LocationEntries;
